import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { FaClipboardCheck, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const RegisterPage4 = ({ onPrevious, onSuccess }) => {
  const LOCAL_KEY = "register_step4";

  const [agreed, setAgreed] = useState(() => {
    const saved = localStorage.getItem(LOCAL_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(agreed));
  }, [agreed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setErrorMessage("You must agree to the disclaimer before submitting.");
      setShowErrorModal(true);
      return;
    }

    try {
      // Build payload from all register_* keys
      const payload = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("register_")) {
          const raw = localStorage.getItem(key);
          let parsed;
          try {
            parsed = JSON.parse(raw);
          } catch {
            parsed = raw;
          }
          if (key === LOCAL_KEY) {
            payload.agreedToDisclaimer = parsed;
          } else if (parsed && typeof parsed === "object") {
            Object.assign(payload, parsed);
          }
        }
      }

      // Extract userId from localStorage
      const userJson = localStorage.getItem("user");
      const userId = userJson ? JSON.parse(userJson).id : null;

      // Choose correct endpoint
      const apiUrl = userId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/registerr_1`
        : `${process.env.REACT_APP_BACKEND_URL}/api/registerr`;

      // If using registerr_1, include createdBy
      if (userId) {
        payload.createdBy = userId;
      }

      // Submit
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Registration failed.");

      // Clear register_* keys
      Object.keys(localStorage)
        .filter((key) => key.startsWith("register_"))
        .forEach((key) => localStorage.removeItem(key));

      setSuccessMessage(
        "Registration successful. Please check your email to activate your account and log in using the password provided."
      );

      setShowSuccessModal(true);
    } catch (err) {
      console.error("Registration Error:", err);
      setErrorMessage(err.message);
      setShowErrorModal(true);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onSuccess && onSuccess();

    const userJson = localStorage.getItem("user");
    const userExists = userJson && Object.keys(JSON.parse(userJson)).length > 0;

    if (!userExists) {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-4 px-3 sm:px-4 lg:px-6">
      <div className="w-full max-w-6xl bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Confirmation & Submission
              </h1>
            </div>

            {/* Show button only if token does NOT exist */}
            {!localStorage.getItem("token") && (
              <button
                onClick={() => {
                  localStorage.clear();
                  navigate("/");
                }}
                className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-white text-blue-700 font-semibold hover:bg-blue-50 hover:shadow transition-all duration-300 text-xs whitespace-nowrap self-center sm:self-auto"
              >
                <span>Already registered?</span>
                <span className="font-bold">Login</span>
              </button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto max-h-[75vh] p-4 sm:p-6 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaShieldAlt className="text-blue-600 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Data Processing Agreement
                </h3>
              </div>
              <div className="text-slate-700 leading-relaxed text-justify text-xs sm:text-sm">
                <p className="mb-3">
                  I agree to GCRD processing personal data contained in this
                  form or other data which GCRD has received from me or may
                  obtain from other people. I agree to the processing of such
                  data for any purposes connected with my studies by GCRD to be
                  shared with partner education institutions.
                </p>
                <p className="mb-3">
                  In addition, I agree to GCRD processing personal data
                  described as Special Category Data as defined under the
                  General Data Protection Regulation, such processing to be
                  undertaken for any purposes as indicated in the declaration
                  above.
                </p>
                <p className="mb-3">
                  I understand and accept this statement. I confirm that the
                  information I have provided on this application is true,
                  complete and accurate and I agree any misinformation may lead
                  to cancellation of my application from GCRD and its partner
                  educational institutions. I confirm that I have declared all
                  previous study and have listed my highest qualification.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={() => setAgreed(!agreed)}
                  className="sr-only" // hides default checkbox
                />
                <span
                  className={`w-5 h-5 flex items-center justify-center border-2 rounded-md transition-colors duration-300
        ${
          agreed
            ? "bg-blue-600 border-blue-600"
            : "border-gray-300 bg-white"
        }`}
                >
                  {agreed && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </span>
                <span className="ml-2 text-sm text-slate-700 font-medium select-none">
                  I Agree to the Terms
                </span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
              {/* Previous Button */}
              <button
                type="button"
                onClick={onPrevious}
                className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-300 flex-1 text-sm"
              >
                Previous
              </button>

              {/* Submit Button with motion */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg font-medium text-white hover:opacity-90 transition-all duration-300 flex-1 text-sm"
              >
                Submit
              </motion.button>
            </div>
          </form>
        </div>
      </div>

      <AnimatePresence>
        {showErrorModal && (
          <Dialog
            open
            onClose={() => setShowErrorModal(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          >
            <Dialog.Panel
              as={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-lg shadow-xl p-5 max-w-sm w-full"
            >
              <Dialog.Title className="text-base font-semibold text-gray-800 mb-3">
                Action Required
              </Dialog.Title>
              <p className="text-sm text-gray-600 mb-4">{errorMessage}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm"
                >
                  OK
                </button>
              </div>
            </Dialog.Panel>
          </Dialog>
        )}

        {showSuccessModal && (
          <Dialog
            open
            onClose={handleSuccessClose}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          >
            <Dialog.Panel
              as={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-lg shadow-xl p-5 max-w-sm w-full text-center"
            >
              <div className="flex justify-center mb-3">
                <FaClipboardCheck className="text-green-600 text-2xl" />
              </div>
              <Dialog.Title className="text-base font-semibold text-gray-800 mb-2">
                Success
              </Dialog.Title>
              <p className="text-sm text-gray-600 mb-4">{successMessage}</p>
              <button
                onClick={handleSuccessClose}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm"
              >
                Continue
              </button>
            </Dialog.Panel>
          </Dialog>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterPage4;